import { useState } from 'react'
import type { FormEvent } from 'react'

import { errorText } from '../../lib/offsite'

/**
 * ชิ้นส่วนที่ใช้ซ้ำในสามหน้าของ "เข้างานนอกสถานที่"
 * (ไม่สร้างระบบ style ใหม่ — ใช้คลาสเดิมจาก src/index.css)
 */

/**
 * กล่องถามเหตุผลก่อนทำสิ่งที่ย้อนไม่ได้ (ยกเลิกใบอนุญาต / ยกเลิกช่วงเวลาเปิด)
 *
 * ทำเป็นกล่องเหนือตารางแทน window.confirm เพราะทั้งสองงานนี้ **บังคับกรอกเหตุผล**
 * (คอลัมน์ revoke_reason / cancel_reason มีไว้ให้ตรวจย้อนหลังได้ว่าใครยกเลิกเพราะอะไร)
 * confirm ของเบราว์เซอร์รับข้อความไม่ได้
 */
export function ReasonPrompt({
  title,
  detail,
  confirmLabel,
  placeholder,
  onConfirm,
  onClose,
}: {
  title: string
  detail: string
  confirmLabel: string
  placeholder: string
  /** โยน error ออกมาได้ — กล่องนี้จะโชว์ให้เองและไม่ปิดตัวเอง */
  onConfirm: (reason: string) => Promise<void>
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trimmed = reason.trim()

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (trimmed.length === 0 || busy) return

    setBusy(true)
    setError(null)
    try {
      await onConfirm(trimmed)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="card">
      <h2>{title}</h2>
      <p className="meta">{detail}</p>
      <form onSubmit={submit}>
        <label>
          เหตุผล (บังคับกรอก)
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={placeholder}
            maxLength={255}
            autoFocus
            required
          />
          <small className="hint">เก็บไว้ในประวัติ ใช้ตอบตอนมีคนถามย้อนหลังว่าทำไมถูกยกเลิก</small>
        </label>

        {error && <p className="error">{error}</p>}

        <div className="row-actions">
          <button type="submit" disabled={trimmed.length === 0 || busy}>
            {busy ? 'กำลังบันทึก…' : confirmLabel}
          </button>
          <button type="button" className="ghost" onClick={onClose} disabled={busy}>
            ไม่ทำ
          </button>
        </div>
      </form>
    </section>
  )
}

/** บอกให้ชัดว่าทำไมหน้านี้ไม่มีปุ่มอะไรเลย ไม่ใช่ปล่อยให้เดาว่าระบบพัง */
export function ViewerNote() {
  return (
    <p className="warn">
      บัญชีนี้มีสิทธิ์ดูข้อมูลเท่านั้น — ปุ่มเพิ่ม/แก้/ยกเลิกจะไม่แสดง
      (ถ้าต้องแก้ ให้ติดต่อผู้ดูแลระบบ)
    </p>
  )
}

/** หัวข้อของตาราง + ปุ่มโหลดใหม่ทางขวา */
export function SectionHead({
  title,
  meta,
  onReload,
  loading,
}: {
  title: string
  meta?: string
  onReload: () => void
  loading: boolean
}) {
  return (
    <div className="section-head">
      <div>
        <h2>{title}</h2>
        {meta && <p className="meta">{meta}</p>}
      </div>
      <button type="button" className="ghost" onClick={onReload} disabled={loading}>
        {loading ? 'กำลังโหลด…' : 'โหลดใหม่'}
      </button>
    </div>
  )
}
